import React, { useMemo } from "react";
import { useLang } from "hooks/useLang";
import { useUserContext } from "context/UserContext";
import { useRouter } from "next/router";
import Link from "next/link";
import { sortPrice } from "utils/helpers/sortEventPrice";
import { hasMultiplePrices } from "utils/helpers/eventPrice";
import { useCartDispatch } from "hooks/useCartV2";
import SAR from "components/Sar/SAR";
import { IFinalEvent } from "types/event";

interface IProps {
    event: IFinalEvent;
}

export default function BuyBtn({
    event,

}: IProps) {
    const { t, isAr } = useLang();
    const { token, user } = useUserContext();
    const router = useRouter();
    const cartDispatch = useCartDispatch();

    const price = useMemo(() => sortPrice(event), [event]);
    const isMultiplePrices = useMemo(() => hasMultiplePrices(event), [event]);

    // Route & Link
    const [pathWithoutQuery, queryString] = router.asPath.split("?");
    const newPath = `${pathWithoutQuery}/book`;
    const href = queryString ? `${newPath}?${queryString}` : newPath;

    const isUserVerified =
        user?.verification?.is_email_verified || user?.verification?.is_phone_verified;

    // Event Expiry
    const isEventExpired = useMemo(
        () => new Date() > new Date(event?.event_date?.ending_date ?? ""),
        [event?.event_date?.ending_date]
    );

    const isTimeSlotsExpired = useMemo(
        () => event?.time_slots?.every((slot) => new Date() > new Date(slot.end_slot)),
        [event?.time_slots]
    );

    const isBookingDisabled = () => {
        const hasNoAvailableTickets = event?.availableTickets?.length === 0;
        const hasNoTimeSlots = event?.time_slots?.length === 0;
        const hasExpiredTimeSlots = !isEventExpired && isTimeSlotsExpired;
        return hasNoAvailableTickets || hasNoTimeSlots || hasExpiredTimeSlots;
    };

    const isButtonDisabled = isBookingDisabled();
    const isBookingEnabled = event?.booking?.is_enable_booking !== false;

    const getButtonLabel = () => {
        if (isBookingEnabled) return t("book_now");
        return isAr
            ? event.booking?.ar_message ?? t("not_available")
            : event.booking?.en_message ?? t("not_available");
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        cartDispatch({ type: "CLEAR_CART" })
        cartDispatch({ type: "SET_EVENT_TITLE", payload: event?.ar_title });

        // if (!token) {
        //     return; // Let Link do its job
        // }

    };

    const priceToDisplay =
        price?.discount?.previous_value != null
            ? price?.discount?.current_value
            : price?.price;

    if (!event?.availableTickets?.length) return null;

    return (
        <div className="flex flex-row gap-2 mt-4 flex-row bg-[#333333B2] md:p-4 md:px-8 px-6 py-3 rounded-full md:rounded-[15px] items-center backdrop-blur-sm md:backdrop-blud-none ">
            {!isEventExpired && !isButtonDisabled && <div className="flex-1 flex flex-col">
                <span className="text-sm text-white">
                    {isMultiplePrices ? t("price_start_from") : t("Ticket_price")}
                </span>
                <div className="flex items-start font-bold">
                    {/* {priceToDisplay ? <SAR textSize={20} price={priceToDisplay} /> : null} */}
                    <SAR textSize={24} width={24} height={24} price={price?.price} />
                </div>
            </div>}

            {/* Book Button */}
            {isEventExpired ? (
                <button
                    disabled
                    className="flex-1 bg-gray-500 text-white rounded-full font-bold px-3 py-1 cursor-not-allowed"
                >
                    {t("event_expired")}
                </button>
            ) : isButtonDisabled ? (
                <button
                    disabled
                    className="flex-1 bg-gray-500 text-white rounded-full font-bold cursor-not-allowed px-4 py-1"
                >
                    {t("no_tickets_available")}
                </button>
            ) : (
                <Link
                    href={href}
                    onClick={isBookingEnabled ? handleLinkClick : (e) => e.preventDefault()}
                    className="flex-1  lg:max-w-[300px]"
                >
                    <button
                        disabled={!isBookingEnabled}
                        className="w-full bg-[#F2EF1D] text-black rounded-full hover:bg-[#f2ee1db8] font-bold py-2 px-4 whitespace-nowrap transition"
                    >
                        {getButtonLabel()}
                    </button>
                </Link>
            )}
        </div>
    );
}
